<script lang="ts">
	import { onMount, tick, onDestroy } from 'svelte';
	import type { CameraZone } from '../types/camera-zones';
	import {
		CANVAS_LINE_WIDTH,
		HANDLE_RADIUS_LARGE,
		darkenColor,
		getRectFromPoints,
		normalizeRectPoints,
		prepareZoneCoordinates,
		prepCoordinatesForZone,
	} from '../helpers/canvas-helpers';
	import { rgbToCss } from 'src/helpers/utilities';

	export let zone: CameraZone | undefined;
	export let zones: CameraZone[] = [];
	export let isPrivacy: boolean = false;

	let canvasEl: HTMLCanvasElement;
	let ctx: CanvasRenderingContext2D | null = null;
	let pointIndex: number = -1;
	let mouseOffsetPerPoint: { x: number; y: number }[] = [];
	let rafId: number | null = null;

	// Internal state
	let editPoints: { xPer: number; yPer: number }[] = [];
	let viewZones: { points: { xPer: number; yPer: number }[]; color: CameraZone['color'] }[] = [];

	$: isViewMode = zones && zones.length > 0;

	// Handle Multiple Display Mode (View)
	$: if (isViewMode) {
		viewZones = zones.map((z) => ({
			points: prepareZoneCoordinates(z.coordinates),
			color: z.color,
		}));
		drawZones();
	}

	// Handle Single Edit Mode (Edit)
	$: if (!isViewMode && zone) {
		initializeEditPoints();
	}

	// React to color/privacy changes in edit mode
	$: if (!isViewMode && (zone?.color || isPrivacy !== undefined)) {
		requestAnimationFrame(drawZones);
	}

	async function initializeEditPoints() {
		if (!zone) return;
		editPoints = prepareZoneCoordinates(zone.coordinates);
		await tick();
		drawZones();
	}

	function scheduleDraw() {
		if (rafId) return;
		rafId = requestAnimationFrame(() => {
			drawZones();
			rafId = null;
		});
	}

	function updateCoordinates() {
		if (zone) {
			zone.coordinates = prepCoordinatesForZone(editPoints);
		}
	}

	// ========================================
	// DRAWING
	// ========================================
	function drawPath(pts: { x: number; y: number }[]) {
		if (!ctx) return;
		ctx.beginPath();
		ctx.moveTo(pts[0].x, pts[0].y);
		for (let i = 1; i < pts.length; i++) {
			ctx.lineTo(pts[i].x, pts[i].y);
		}
		ctx.closePath();
	}

	function drawHandle(x: number, y: number, isActive: boolean = false) {
		if (!ctx) return;
		ctx.beginPath();
		ctx.arc(x, y, HANDLE_RADIUS_LARGE, 0, 2 * Math.PI);
		ctx.closePath();

		ctx.fillStyle = '#ffffff';
		if (isActive) {
			ctx.fill();
			ctx.lineWidth = 8;
			ctx.strokeStyle = '#3399ff';
			ctx.stroke();
		} else {
			ctx.fill();
		}

		const fontSize = isActive ? 42 : 36;
		ctx.fillStyle = isActive ? '#3399ff' : '';

		ctx.save();
		ctx.translate(x, y);
		ctx.rotate(Math.PI / 2);
		ctx.font = `900 ${fontSize}px "Font Awesome 7 Pro"`;
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';
		ctx.fillText('\uf424', 0, 0);
		ctx.restore();
	}

	function drawZoneShape(
		pts: { xPer: number; yPer: number }[],
		c: CameraZone['color'],
		w: number,
		h: number,
		privacy: boolean = false,
	) {
		if (!ctx || pts.length === 0) return;

		drawPath(
			getRectFromPoints(pts).map((p) => ({
				x: p.xPer * w,
				y: p.yPer * h,
			})),
		);
		ctx.fillStyle = privacy ? rgbToCss(darkenColor(c), 0.7) : rgbToCss(c, 0.2);
		ctx.fill();
		if (!isViewMode) {
			ctx.strokeStyle = rgbToCss(c, 1);
			ctx.stroke();
		}
	}

	function drawZones() {
		if (!ctx || !canvasEl) return;

		ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);
		ctx.lineWidth = CANVAS_LINE_WIDTH;

		const w = canvasEl.width;
		const h = canvasEl.height;

		if (isViewMode) {
			viewZones.forEach((z) => {
				drawZoneShape(z.points, z.color, w, h);
			});
		} else if (zone) {
			drawZoneShape(editPoints, zone.color, w, h, isPrivacy);
			for (let i = 0; i < editPoints.length; i++) {
				if (i % 2 === 1) continue; // Only TL and BR handles
				const x = editPoints[i].xPer * w;
				const y = editPoints[i].yPer * h;
				drawHandle(x, y, i === pointIndex);
			}
		}
	}

	// ========================================
	// INTERACTION
	// ========================================
	function getMousePosition(e: MouseEvent) {
		const rect = canvasEl.getBoundingClientRect();
		return {
			x: (e.clientX - rect.left) * (canvasEl.width / rect.width),
			y: (e.clientY - rect.top) * (canvasEl.height / rect.height),
			rect,
		};
	}

	function checkPointSelection(mx: number, my: number, w: number, h: number): number {
		if (!ctx) return -1;
		for (let i = 0; i < editPoints.length; i++) {
			if (i % 2 === 1) continue;
			const x = editPoints[i].xPer * w;
			const y = editPoints[i].yPer * h;
			ctx.beginPath();
			ctx.arc(x, y, HANDLE_RADIUS_LARGE, 0, 2 * Math.PI);
			ctx.closePath();
			if (ctx.isPointInPath(mx, my)) return i;
		}
		return -1;
	}

	function checkZoneBodySelection(mx: number, my: number, w: number, h: number): boolean {
		if (!ctx) return false;
		drawPath(
			getRectFromPoints(editPoints).map((p) => ({
				x: p.xPer * w,
				y: p.yPer * h,
			})),
		);
		return ctx.isPointInPath(mx, my);
	}

	function onPointerDown(e: MouseEvent) {
		if (isViewMode) return;
		if (editPoints.length === 0) return;

		const { x, y } = getMousePosition(e);
		const w = canvasEl.width;
		const h = canvasEl.height;

		pointIndex = checkPointSelection(x, y, w, h);
		if (pointIndex !== -1) {
			drawZones();
			return;
		}

		if (checkZoneBodySelection(x, y, w, h)) {
			mouseOffsetPerPoint = editPoints.map((p) => ({
				x: x - p.xPer * w,
				y: y - p.yPer * h,
			}));
		}
	}

	function onPointerMove(e: MouseEvent) {
		if (isViewMode) return;
		if (e.buttons !== 1 || editPoints.length === 0) return;

		e.preventDefault();
		const { x, y } = getMousePosition(e);
		const w = canvasEl.width;
		const h = canvasEl.height;

		if (pointIndex !== -1) {
			editPoints[pointIndex] = { xPer: x / w, yPer: y / h };
			editPoints = normalizeRectPoints(editPoints, pointIndex);
		} else if (mouseOffsetPerPoint.length > 0) {
			let valid = true;
			for (let i = 0; i < editPoints.length; i++) {
				const px = x - mouseOffsetPerPoint[i].x;
				const py = y - mouseOffsetPerPoint[i].y;
				if (px < 0 || px > w || py < 0 || py > h) {
					valid = false;
					break;
				}
			}
			if (valid) {
				for (let i = 0; i < editPoints.length; i++) {
					editPoints[i] = {
						xPer: (x - mouseOffsetPerPoint[i].x) / w,
						yPer: (y - mouseOffsetPerPoint[i].y) / h,
					};
				}
			}
		}
		scheduleDraw();
	}

	function onPointerUp(e: MouseEvent) {
		if (isViewMode) return;
		pointIndex = -1;
		mouseOffsetPerPoint = [];
		updateCoordinates();
		drawZones();
	}

	function onContextMenu(e: MouseEvent) {
		e.preventDefault();
		return false;
	}

	onMount(() => {
		if (canvasEl) {
			ctx = canvasEl.getContext('2d');
			if (editPoints.length > 0 || viewZones.length > 0) drawZones();
		}
	});

	onDestroy(() => {
		if (rafId) cancelAnimationFrame(rafId);
	});
</script>

<div class="zone-holder">
	<canvas
		bind:this={canvasEl}
		width="1920"
		height="1080"
		class={'zone-canvas ' + (isViewMode ? 'view' : 'edit')}
		on:pointerdown={onPointerDown}
		on:pointermove={onPointerMove}
		on:pointerup={onPointerUp}
		on:contextmenu={onContextMenu}
	></canvas>
	<slot />
</div>

<style>
	.zone-holder {
		position: relative;
		width: 100%;
		height: auto;
		aspect-ratio: 16 / 9;
		border-radius: var(--radius-big);
		overflow: hidden;
	}

	.zone-canvas {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		z-index: 10;
		touch-action: none;
	}

	.zone-canvas.view {
		pointer-events: none;
	}
</style>
